/* convertDiskStorage function takes in value as GB (Ex:1000GB) and convert it to its appropriate (Ex: 1TB) TB/PB/EB/ZB*/
import { ChartData } from './types';

function convertDiskStorage(value: number, usedValueInKB: number) {
  try {
    if (typeof value !== 'number') {
      return 0;
    }

    if (value === 0) {
      if (usedValueInKB && usedValueInKB < 1024) {
        return usedValueInKB.toFixed(2) + ' KB';
      } else {
        return '0';
      }
    }

    const sizes = ['MB', 'GB', 'TB', 'PB', 'EB', 'ZB'];
    let updatedValue = value;
    let unitIndex = 1; // Start from GB

    if (value < 1) {
      updatedValue = value * 1000; // Convert GB to MB
      unitIndex = 0; // MB
      if (updatedValue < 0.01) {
        return '0';
      }
    } else {
      while (updatedValue >= 1000 && unitIndex < sizes.length - 1) {
        updatedValue /= 1000;
        unitIndex++;
      }
    }
    return updatedValue.toFixed(2) + ' ' + sizes[unitIndex];
  } catch (error) {
    console.error('Failed to calculate storage', error);
  }

  return null;
}

export function convertStorageValuesToNumbers(value: string) {
  const storageValue = value.slice(0, -2);
  return Number(storageValue);
}

export function getTotalValue(totalValue: string, type: string) {
  let computedTotalValue = totalValue === 'Unlimited' ? 0 : totalValue;
  if (computedTotalValue !== 0) {
    computedTotalValue =
      type === 'storage'
        ? convertStorageValuesToNumbers(computedTotalValue as string)
        : (computedTotalValue as number);
  }
  return computedTotalValue;
}

export function getValues(data: ChartData[], type: string) {
  const usedValue = data.filter((item) => item.label === 'Used')[0].value;
  const totalValue = data.filter((item) => item.label === 'Total')[0].value;
  const usedValueInKB = data.filter((item) => item.label === 'Used')[0]
    .usedValueInKB;

  return {
    usedValue:
      type === 'storage'
        ? convertStorageValuesToNumbers(usedValue as string)
        : (usedValue as number),
    totalValue: getTotalValue(totalValue as string, type),
    customLegendUsedValue: getUsedAndTotalLegendValues(
      usedValue as number,
      type,
      usedValueInKB ?? 0,
    ),
    customLegendTotalValue:
      totalValue === 'Unlimited'
        ? totalValue
        : getUsedAndTotalLegendValues(totalValue as number, type, 0),
  };
}

export const getUsedAndTotalLegendValues = (
  value: string | number,
  type: string,
  usedValueInKB: number,
) => {
  return type === 'storage'
    ? convertDiskStorage(
        convertStorageValuesToNumbers(value as string),
        usedValueInKB,
      )
    : value;
};

/**
 * Transforms chart data into a format suitable for pie chart rendering.
 *
 * @param data - Array of chart data objects containing label and legend information
 * @param type - The type of chart data to process
 * @returns An array of objects with id, label, and value properties for pie chart consumption
 *
 * @remarks
 * - When totalValue is 0 (unlimited case), used space remains 0 and available space is set to 0.1 to display an empty pie
 * - For non-zero total values, calculates actual used and available space based on the difference
 * - Maps 'Used' label items to computed used space, all others to computed available space
 */
export function getChartData(data: ChartData[], type: string) {
  const { usedValue, totalValue } = getValues(data, type);
  const computedUsedSpace = totalValue === 0 ? totalValue : usedValue;
  // Assigning  0.1 to Total to show empty pie for Unlimited case
  const computedAvailableSpace =
    totalValue === 0 ? 0.1 : (totalValue as number) - (usedValue as number);

  return data.map((item) => ({
    id: item.label,
    label: item.legendLabel,
    value: item.label === 'Used' ? computedUsedSpace : computedAvailableSpace,
  }));
}
